import {
    CLIENT_BRAIN_FIELD_TYPES,
    CLIENT_BRAIN_GOAL_STATUSES,
    CLIENT_BRAIN_SECTION_KINDS,
    ClientBrainDocument,
    ClientBrainField,
    ClientBrainFieldType,
    ClientBrainGoal,
    ClientBrainGoalAction,
    ClientBrainGoalStatus,
    ClientBrainInstance,
    ClientBrainRisk,
    ClientBrainSection,
    ClientBrainSectionKind,
    ClientBrainTemplate,
    ClientBrainValue,
    EMPTY_CLIENT_BRAIN_DOCUMENT,
    emptyClientBrainInstance
} from "./clientBrainTypes";
import { clampDescription, clampTitle, clampValue } from "./clientBrainSchema";

function newId(prefix: string): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return `${prefix}-${crypto.randomUUID()}`;
    }
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function asString(value: unknown): string {
    return typeof value === "string" ? value : "";
}

function asNumberOrNull(value: unknown): number | null {
    return typeof value === "number" ? value : null;
}

function asStringOrNull(value: unknown): string | null {
    return typeof value === "string" ? value : null;
}

export function cloneDocument(document: ClientBrainDocument): ClientBrainDocument {
    return JSON.parse(JSON.stringify(document)) as ClientBrainDocument;
}

export function cloneTemplate(template: ClientBrainTemplate): ClientBrainTemplate {
    return JSON.parse(JSON.stringify(template)) as ClientBrainTemplate;
}

export function cloneInstance(instance: ClientBrainInstance): ClientBrainInstance {
    return JSON.parse(JSON.stringify(instance)) as ClientBrainInstance;
}

export function documentsEqual(left: ClientBrainDocument, right: ClientBrainDocument): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

export function templatesEqual(left: ClientBrainTemplate | null, right: ClientBrainTemplate | null): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

export function instancesEqual(left: ClientBrainInstance, right: ClientBrainInstance): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

export function newField(): ClientBrainField {
    return {
        id: newId("cbf"),
        label: "",
        description: "",
        field_type: "short_text",
        options: [],
        required: false
    };
}

export function newSection(kind: ClientBrainSectionKind = "fields"): ClientBrainSection {
    return {
        id: newId("cbs"),
        title: "",
        description: "",
        kind: CLIENT_BRAIN_SECTION_KINDS.includes(kind) ? kind : "fields",
        fields: []
    };
}

export function newTemplate(): ClientBrainTemplate {
    return {
        id: newId("cbt"),
        title: "",
        sections: []
    };
}

export function newGoalAction(): ClientBrainGoalAction {
    return {
        id: newId("cba"),
        description: "",
        responsible: "",
        timeframe: ""
    };
}

export function newGoal(): ClientBrainGoal {
    return {
        id: newId("cbg"),
        title: "",
        description: "",
        methods: "",
        signs: "",
        status: "active",
        actions: []
    };
}

export function newRisk(): ClientBrainRisk {
    return {
        id: newId("cbr"),
        title: "",
        description: ""
    };
}

function normalizeFieldType(value: unknown): ClientBrainFieldType {
    return CLIENT_BRAIN_FIELD_TYPES.includes(value as ClientBrainFieldType) ? (value as ClientBrainFieldType) : "short_text";
}

function normalizeSectionKind(value: unknown): ClientBrainSectionKind {
    return CLIENT_BRAIN_SECTION_KINDS.includes(value as ClientBrainSectionKind) ? (value as ClientBrainSectionKind) : "fields";
}

function normalizeGoalStatus(value: unknown): ClientBrainGoalStatus {
    return CLIENT_BRAIN_GOAL_STATUSES.includes(value as ClientBrainGoalStatus) ? (value as ClientBrainGoalStatus) : "active";
}

function normalizeField(data: unknown): ClientBrainField {
    const raw = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
    return {
        id: asString(raw.id) || newId("cbf"),
        label: clampTitle(asString(raw.label)),
        description: clampDescription(asString(raw.description)),
        field_type: normalizeFieldType(raw.field_type),
        options: Array.isArray(raw.options) ? raw.options.filter(option => typeof option === "string").map(option => clampTitle(option as string)) : [],
        required: raw.required === true
    };
}

function normalizeSection(data: unknown): ClientBrainSection {
    const raw = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
    return {
        id: asString(raw.id) || newId("cbs"),
        title: clampTitle(asString(raw.title)),
        description: clampDescription(asString(raw.description)),
        kind: normalizeSectionKind(raw.kind),
        fields: Array.isArray(raw.fields) ? raw.fields.map(normalizeField) : []
    };
}

export function normalizeTemplate(data: unknown): ClientBrainTemplate | null {
    if (!data || typeof data !== "object") {
        return null;
    }
    const raw = data as Record<string, unknown>;
    return {
        id: asString(raw.id) || newId("cbt"),
        title: clampTitle(asString(raw.title)),
        sections: Array.isArray(raw.sections) ? raw.sections.map(normalizeSection) : []
    };
}

export function normalizeDocument(data: unknown): ClientBrainDocument {
    if (!data || typeof data !== "object") {
        return cloneDocument(EMPTY_CLIENT_BRAIN_DOCUMENT);
    }
    const raw = data as Record<string, unknown>;
    return {
        schema_version: typeof raw.schema_version === "number" ? raw.schema_version : 1,
        draft: normalizeTemplate(raw.draft),
        published: normalizeTemplate(raw.published),
        published_at: asNumberOrNull(raw.published_at),
        published_by: asStringOrNull(raw.published_by),
        updated_at: asNumberOrNull(raw.updated_at),
        updated_by: asStringOrNull(raw.updated_by)
    };
}

function normalizeValue(value: unknown): ClientBrainValue | undefined {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "string") {
        return clampValue(value);
    }
    if (Array.isArray(value)) {
        return value.filter(item => typeof item === "string").map(item => clampValue(item as string));
    }
    return undefined;
}

function normalizeGoalAction(data: unknown): ClientBrainGoalAction {
    const raw = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
    return {
        id: asString(raw.id) || newId("cba"),
        description: clampDescription(asString(raw.description)),
        responsible: clampTitle(asString(raw.responsible)),
        timeframe: clampTitle(asString(raw.timeframe))
    };
}

function normalizeGoal(data: unknown): ClientBrainGoal {
    const raw = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
    return {
        id: asString(raw.id) || newId("cbg"),
        title: clampTitle(asString(raw.title)),
        description: clampDescription(asString(raw.description)),
        methods: clampDescription(asString(raw.methods)),
        signs: clampDescription(asString(raw.signs)),
        status: normalizeGoalStatus(raw.status),
        actions: Array.isArray(raw.actions) ? raw.actions.map(normalizeGoalAction) : []
    };
}

function normalizeRisk(data: unknown): ClientBrainRisk {
    const raw = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
    return {
        id: asString(raw.id) || newId("cbr"),
        title: clampTitle(asString(raw.title)),
        description: clampDescription(asString(raw.description))
    };
}

export function normalizeInstance(data: unknown): ClientBrainInstance {
    const instance = emptyClientBrainInstance();
    if (!data || typeof data !== "object") {
        return instance;
    }
    const raw = data as Record<string, unknown>;

    if (typeof raw.version === "number" && Number.isInteger(raw.version) && raw.version >= 0) instance.version = raw.version;
    instance.schema_version = typeof raw.schema_version === "number" ? raw.schema_version : 1;
    instance.template_id = asStringOrNull(raw.template_id);
    instance.template_published_at = asNumberOrNull(raw.template_published_at);
    instance.updated_at = asNumberOrNull(raw.updated_at);
    instance.updated_by = asStringOrNull(raw.updated_by);

    if (raw.values && typeof raw.values === "object") {
        for (const [fieldId, value] of Object.entries(raw.values as Record<string, unknown>)) {
            const normalized = normalizeValue(value);
            if (normalized !== undefined) {
                instance.values[fieldId] = normalized;
            }
        }
    }

    if (raw.goals && typeof raw.goals === "object") {
        for (const [sectionId, goals] of Object.entries(raw.goals as Record<string, unknown>)) {
            instance.goals[sectionId] = Array.isArray(goals) ? goals.map(normalizeGoal) : [];
        }
    }

    if (raw.risks && typeof raw.risks === "object") {
        for (const [sectionId, risks] of Object.entries(raw.risks as Record<string, unknown>)) {
            instance.risks[sectionId] = Array.isArray(risks) ? risks.map(normalizeRisk) : [];
        }
    }

    return instance;
}
